import { useMemo } from 'react';
import { ProductType, SavingsCalculatorStateType } from '../types';

const DEFAULT_DATA_OBJ = {
    year: 0,
    totalValue: 0,
    deposits: 0,
};

type GraphData = typeof DEFAULT_DATA_OBJ;

const DEFAULT_GRAPH_DATA = Array.from(Array(16), (_, index) => ({
    ...DEFAULT_DATA_OBJ,
    year: index,
}));

export const formatInterestRate = (rate: number | string) => parseFloat(`${rate}`.replaceAll(/[^0-9.,-]/g, '').replace(',', '.'));

export const useCalculate = (
    { startAmount, monthlyDeposit, annualInterestRate, customInterestRate, years }: SavingsCalculatorStateType,
    chosenProduct: ProductType | null = null
) => {
    const interestRate = useMemo(
        () => (chosenProduct ? annualInterestRate : formatInterestRate(customInterestRate)),
        [customInterestRate, annualInterestRate, chosenProduct]
    );

    const calculations = useMemo(() => {
        if (!interestRate || startAmount === null || !years) {
            return {
                futureTotalValue: 0,
                totalDeposits: 0,
                totalInterestEarned: 0,
                graphData: DEFAULT_GRAPH_DATA,
            };
        }

        const dailyRate = interestRate / 100 / 365;
        const graphData: GraphData[] = [];

        // Track balance year by year with annual interest crediting
        let balance = startAmount;
        let totalDepositsAmount = startAmount;

        // Year 0 = "now" / starting point before any interest or deposits
        graphData.push({ year: 0, totalValue: balance, deposits: totalDepositsAmount });

        for (let year = 1; year <= years; year++) {
            // Calculate interest for start-of-year balance (full year)
            // Interest credited on Dec 31st, so balance earns interest for 365 days
            const interestOnBalance = balance * dailyRate * 365;

            // Calculate interest for each monthly deposit based on days remaining in year
            // Assume deposits on last day of each month
            const daysToMonthEnd = [31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334, 365];
            let interestOnDeposits = 0;
            for (let month = 0; month < 12; month++) {
                const daysRemaining = 365 - daysToMonthEnd[month];
                interestOnDeposits += (monthlyDeposit || 0) * dailyRate * daysRemaining;
            }

            // Credit interest and add deposits at end of year
            const yearlyDeposits = (monthlyDeposit || 0) * 12;
            balance = balance + interestOnBalance + yearlyDeposits + interestOnDeposits;
            totalDepositsAmount += yearlyDeposits;

            graphData.push({ year, totalValue: balance, deposits: totalDepositsAmount });
        }

        const finalData = graphData[graphData.length - 1];

        return {
            futureTotalValue: finalData.totalValue,
            totalDeposits: finalData.deposits,
            totalInterestEarned: finalData.totalValue - finalData.deposits,
            graphData,
        };
    }, [startAmount, monthlyDeposit, interestRate, years]);

    return calculations;
};
